import { DynamicPageProps } from '@doltech/routeDefinition';
import { dolDinhLucAuthor } from '@doltech/ui/lib/figma/Blog/dolDinhLucAuthor.data';
import { LandingBlogDetailLayout } from '@doltech/ui/lib/figma/Blog/LandingBlogDetailLayout';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { useLandingSharedDataStore } from '@doltech/ui/lib/figma/Layout/Landing/model/landing-shared-data.hook';
import {
  BlogSocialButton,
  BlogSocialButtonPreview,
} from '@doltech/ui/lib/figma/Layout/Landing/SocialButton/BlogSocialButton';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { BlogType } from '@doltech/utils/lib/blog/constants';
import { PageTypeEnum } from '@doltech/utils/lib/constants';
import { getLengthEnumTextByKey } from '@doltech/utils/lib/enum/LengthEnum';
import { calculateTableOfContent } from '@doltech/utils/lib/url';
import * as React from 'react';
import styled from 'styled-components';

import { PreviewDescriptionEditor } from '@doltech/ui/lib/shared/components/DescriptionEditor/View/PreviewDescriptionEditor';
import dynamic from 'next/dynamic';
import { BLOG_SECTION_PREFIX } from '../../../blog-management-constant';
import { usePageObjectContent } from '../../../hook/usePageObjectContent.hook';
import { shouldRenderOnScreen } from '@doltech/ui/lib/hocs/withShouldRenderOnScreen';
import { compose } from '@doltech/utils/lib/compose';
import { withSuspense } from '@doltech/ui/lib/hocs/withSuspense';

const IEBlogSticky = compose(
  shouldRenderOnScreen({ fromScreen: 1280 }),
  withSuspense
)(
  dynamic<any>(
    () => import('../../../component/IELTSBlogStickyComponent').then((mod) => mod.IEBlogSticky),
    {
      ssr: true,
    }
  )
);
const BlogRelatedPosts = dynamic<any>(
  () => import('./BlogRelatedPosts').then((mod) => mod.BlogRelatedPosts),
  {
    ssr: true,
  }
);
const BlogEditorReadOnlyWithStyles = dynamic<any>(
  () =>
    import('@doltech/ui/lib/figma/BlogEditor/BlogEditorReadOnlyWithStylesPlugin').then(
      (mod) => mod.BlogEditorReadOnlyWithStyles
    ),
  {
    ssr: true,
  }
);

const convertData = (children) => {
  const data = [];
  children?.forEach((item) => {
    if (item.type === 'h2') {
      data.push(item);
    }
    if (item.type === 'h3' && data[data.length - 1]) {
      if (!data[data.length - 1].sub) {
        data[data.length - 1].sub = [];
      }
      data[data.length - 1].sub.push(item);
    }
  });
  return data;
};

// const BlogEditorReadOnlyWithStyles = dynamic<any>(
//   () =>
//     import('@doltech/ui/lib/figma/BlogEditor/BlogEditorReadOnlyWithStylesPlugin').then(
//       (mod) => mod.BlogEditorReadOnlyWithStyles
//     ),
//   {
//     ssr: true,
//   }
// );
// const BlogEditorReadOnlyWithStyles = dynamic<any>(
//   () =>
//     import('@doltech/ui/lib/figma/BlogEditor/BlogEditorReadOnlyV2').then(
//       (mod) => mod.BlogEditorReadOnly
//     )
// );

const StyledLandingBlogDetailLayout = styled(LandingBlogDetailLayout)`
  #blog-cover {
    img {
      border-radius: 16px;
    }
  }

  .icon-clap {
    svg {
      transform: scaleX(-1);
      margin-top: 5px;
    }
  }

  #blog-header {
    .dol-editor {
      font-size: 18px;
      line-height: 28.8px;
      color: ${colorsV2.black70};
      @media ${deviceBreakpoints.fromTablet} {
        font-size: 20px;
        line-height: 32px;
      }
    }
  }

  && {
    #layout-landing-content {
      padding-bottom: 0;
    }
  }
`;

export const getTableOfContentForView = ({ tableOfContent, tableOfContentHidden }) => {
  const data = [];
  let toc = null;
  try {
    toc = JSON.parse(tableOfContent);
    // eslint-disable-next-line no-empty
  } catch (ex) {}

  if (toc) {
    convertData(toc).forEach((item, index) => {
      const contentItem = {
        id: `${BLOG_SECTION_PREFIX}${index + 1}`,
        item,
        sub: undefined,
      };
      item.h3Child?.forEach((cItem, cIndex) => {
        const subId = `${BLOG_SECTION_PREFIX}${index + 1}-${cIndex + 1}`;
        if (!tableOfContentHidden?.includes(subId)) {
          if (!contentItem.sub) {
            contentItem.sub = [];
          }
          contentItem.sub.push({
            id: subId,
            item: cItem,
          });
        }
      });
      data.push(contentItem);
    });
  }
  return data;
};

interface BlogPreviewProps extends DynamicPageProps {
  detailData: any;
  relatedPosts?: any;
}

export const BlogPreviewBase = (props: BlogPreviewProps) => {
  const { pageData, detailData, seoData, relatedPosts, internalPageLink } = props;

  const store = useLandingSharedDataStore();
  const isPreview = !store;
  const pageType = React.useMemo(() => {
    if (seoData?.type) {
      return seoData?.type;
    }
    switch (detailData?.blogType) {
      case BlogType.GRAMMAR: {
        return PageTypeEnum.GRAMMAR_IELTS_BLOG;
      }
      case BlogType.VOCAB: {
        return PageTypeEnum.VOCAB_IELTS_BLOG;
      }
      case BlogType.TEACHER_REVIEW:
      case BlogType.COURSE_REVIEW:
      case BlogType.CENTER_REVIEW: {
        return PageTypeEnum.CENTER_REVIEW_IELTS_BLOG;
      }
      case BlogType.BOOK_REVIEW: {
        return PageTypeEnum.BOOK_REVIEW_IELTS_BLOG;
      }
      case BlogType.IELTS_INFO: {
        return PageTypeEnum.IELTS_INFO_BLOG;
      }
      case BlogType.DOL_NEWS: {
        return PageTypeEnum.DOL_NEWS_IELTS_BLOG;
      }
      case BlogType.LINEARTHINKING: {
        return PageTypeEnum.DOL_LINEAR_IELTS_BLOG;
      }
      case BlogType.STUDENT_STORY: {
        return PageTypeEnum.STUDENT_STORY_IELTS_BLOG;
      }
      default:
    }
  }, [detailData, seoData]);

  const pageSeoInfo = usePageObjectContent(pageType as PageTypeEnum, internalPageLink);
  const {
    header,
    blogLength,
    author,
    cover,
    thumbnail,
    tocTitle,
    body,
    tableOfContent,
    tableOfContentHidden,
    isTocShown,
  } = detailData;

  const tocForView = React.useMemo(() => {
    return getTableOfContentForView({ tableOfContent, tableOfContentHidden });
  }, [tableOfContent, tableOfContentHidden]);
  const editorWrapperRef = React.useRef();

  const sameAsArr = seoData?.blogPosting?.schema?.sameAs;
  return (
    <StyledLandingBlogDetailLayout
      seoData={seoData}
      layoutTopBar={null}
      useSideBar={!isPreview}
      headerEditor={
        <PreviewDescriptionEditor
          className="header-editor"
          initialValue={React.useMemo(() => (header ? JSON.parse(header) : null), [header])}
        />
      }
      header={React.useMemo(
        () => ({
          routes: pageSeoInfo?.routes || [
            {
              path: '/',
              breadcrumbName: 'Trang chủ',
            },
            {
              path: internalPageLink?.staticPageLink?.LANDING_IELTS_KNOWLEDGE?.url || '/',
              breadcrumbName: 'Kiến thức IELTS',
            },
            {
              breadcrumbName: pageSeoInfo?.title,
              path: pageSeoInfo?.url,
            },
          ],
          readTimeInMinutes: blogLength ? getLengthEnumTextByKey(blogLength) : undefined,
          author: {
            ...dolDinhLucAuthor,
            name: author,
          },
          publishedDate: seoData
            ? new Date(seoData.createdAt || seoData.lastModifiedAt)
            : new Date(),
        }),
        [
          author,
          blogLength,
          internalPageLink?.staticPageLink?.LANDING_IELTS_KNOWLEDGE?.url,
          pageSeoInfo?.routes,
          pageSeoInfo?.title,
          pageSeoInfo?.url,
          seoData,
        ]
      )}
      coverEditor={React.useMemo(
        () => ({
          text: cover?.caption || thumbnail?.caption,
          src: cover?.url || thumbnail?.url,
          width: cover?.width || 752,
          height: cover?.height || 424,
          alt: cover?.alt || thumbnail?.alt,
        }),
        [
          cover?.alt,
          cover?.caption,
          cover?.height,
          cover?.url,
          cover?.width,
          thumbnail?.alt,
          thumbnail?.caption,
          thumbnail?.url,
        ]
      )}
      title={tocTitle}
      customSticky={<IEBlogSticky seoData={seoData} {...pageSeoInfo} />}
      socials={seoData ? BlogSocialButton : BlogSocialButtonPreview}
      tableOfContentEditor={isTocShown ? tocForView : undefined}
      contentFooter={
        !isPreview &&
        relatedPosts && <BlogRelatedPosts pageSeoInfo={pageSeoInfo} relatedPosts={relatedPosts} />
      }
    >
      <div ref={editorWrapperRef} className="responsive-blog-section" id="blog-preview-content">
        <BlogEditorReadOnlyWithStyles
          onMounted={() => {
            if (editorWrapperRef.current) {
              calculateTableOfContent(tocForView);
            }
          }}
          pageData={pageData}
          seoData={seoData}
          initialValue={React.useMemo(() => (body ? JSON.parse(body) : null), [body])}
        />
      </div>
      {sameAsArr && sameAsArr.length > 0 && (
        <p style={{ display: 'none' }}>
          Link tham khảo:
          {sameAsArr.map((sameAs, index) => (
            <React.Fragment key={index}>
              <br />
              {sameAs}
            </React.Fragment>
          ))}
        </p>
      )}
    </StyledLandingBlogDetailLayout>
  );
};
